import { useEffect, useRef, useState } from 'react';
import { useArchitectureMap } from '@/api/endpointsArchitecture';
import { useMe } from '@/api/hooks';
import { config } from '@/config';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { effectiveTheme, frameOrigin, frameSrc, type FrameTheme } from '@/features/architecture/architectureModel';
import { formatDate } from '@/lib/format';

/**
 * Hangar's own architecture (plan P25): the map the archmap skill renders from the code, every fact with its evidence.
 * Hangar administration only: it names internal paths and infrastructure, so the API refuses anyone but an operator.
 * The map runs in a frame from the API's origin (a 15-minute signed link), isolated from the console.
 */
export function ArchitecturePage() {
  const me = useMe();
  const operator = me.data?.user?.platformRole === 'operator';
  const map = useArchitectureMap(operator);
  return (
    <div data-testid="architecture-page" className="flex min-w-0 flex-col gap-4">
      <header className="enter flex flex-col gap-1 border-b pb-4">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Architecture</h1>
        <p className="max-w-[72ch] text-sm text-muted-foreground">
          How Hangar is built, from its code: the parts, how they connect, request flows step by step and what each part needs to run.
          {map.data?.renderedAt ? <span className="text-subtle"> Generated {formatDate(map.data.renderedAt)}.</span> : null}
        </p>
      </header>
      {me.isPending || (operator && map.isPending) ? <Skeleton data-testid="architecture-loading" className="h-[calc(100dvh-14rem)] min-h-[420px] w-full rounded-xl" /> : null}
      {me.data && !operator ? <Locked /> : null}
      {map.isError ? <ErrorState error={map.error} onRetry={() => void map.refetch()} title="Couldn't open the architecture map" /> : null}
      {map.data && !map.data.available ? <NotRendered /> : null}
      {map.data?.available && map.data.url ? <MapFrame link={map.data.url} /> : null}
    </div>
  );
}

function Locked() {
  return (
    <Card className="enter">
      <EmptyState testId="architecture-locked" icon="lock" title="Only platform operators see Hangar's architecture" description="It describes Hangar's own code and infrastructure." />
    </Card>
  );
}

function NotRendered() {
  return (
    <Card className="enter">
      <EmptyState testId="architecture-empty" icon="account_tree" title="The architecture map is not generated yet" description="Generate it with the archmap skill (docs/architecture/hangar.html), then open this page again." />
    </Card>
  );
}

const currentTheme = (): FrameTheme => effectiveTheme(document.documentElement.dataset.theme, window.matchMedia('(prefers-color-scheme: dark)').matches);

/** The frame, sized to the viewport; it follows the console's theme while open. */
function MapFrame({ link }: { link: string }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [src] = useState(() => frameSrc(link, config.apiUrl, currentTheme()));
  useEffect(() => {
    const post = () => frame.current?.contentWindow?.postMessage({ type: 'archmap:theme', theme: currentTheme() }, frameOrigin(config.apiUrl));
    const observer = new MutationObserver(post);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', post);
    return () => { observer.disconnect(); media.removeEventListener('change', post); };
  }, []);
  return (
    <iframe
      ref={frame}
      data-testid="architecture-frame"
      title="Hangar architecture map"
      src={src}
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"
      allow="clipboard-write"
      referrerPolicy="no-referrer"
      className="enter h-[calc(100dvh-14rem)] min-h-[420px] w-full rounded-xl border bg-card max-sm:h-[calc(100dvh-16rem)]"
    />
  );
}
